/**
 * Thin wrapper around Firebase Analytics (GA4-compatible) for our e-commerce
 * events. Every public function is a no-op when Analytics isn't available
 * (SSR, browsers that don't support it, missing measurementId), so callers
 * never have to guard.
 *
 * Event names follow GA4's standard e-commerce schema where possible:
 *   page_view, view_item, view_item_list, select_item, add_to_cart,
 *   begin_checkout, purchase, search.
 */
import { logEvent } from 'firebase/analytics';
import { analyticsPromise } from '../firebase';
import api from '../api/axios';

async function safeLog(eventName, params) {
  try {
    const analytics = await analyticsPromise;
    if (!analytics) return;
    logEvent(analytics, eventName, params || {});
  } catch {
    // Never let analytics break the app.
  }
}

// ---- helpers ----

function toItem(product, options = {}) {
  if (!product) return null;
  return {
    item_id: product.id,
    item_name: product.name,
    item_category: product.category_ids?.[0] || product.category_id || undefined,
    price: typeof product.price === 'number' ? product.price : undefined,
    quantity: options.quantity || 1,
    item_variant: options.variantName || product.variant_name || undefined,
  };
}

function cartItemToItem(item) {
  return {
    item_id: item.product_id,
    item_name: item.product_name,
    item_variant: item.variant_name || undefined,
    price: typeof item.price === 'number' ? item.price : undefined,
    quantity: item.quantity || 1,
  };
}

// ---- events ----

export function trackPageView(path, title) {
  safeLog('page_view', {
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    page_title: title || (typeof document !== 'undefined' ? document.title : undefined),
  });
}

export function trackSearch(searchTerm) {
  if (!searchTerm) return;
  safeLog('search', { search_term: searchTerm });
}

export function trackViewItem(product) {
  const item = toItem(product);
  if (!item) return;
  safeLog('view_item', {
    currency: 'USD',
    value: item.price,
    items: [item],
  });
}

export function trackViewItemList(products, listName) {
  if (!products?.length) return;
  safeLog('view_item_list', {
    item_list_name: listName,
    items: products.map((p) => toItem(p)).filter(Boolean),
  });
}

export function trackSelectItem(product, listName) {
  const item = toItem(product);
  if (!item) return;
  safeLog('select_item', {
    item_list_name: listName,
    items: [item],
  });
}

export function trackAddToCart(product, quantity, variantName) {
  const item = toItem(product, { quantity, variantName });
  if (!item) return;
  safeLog('add_to_cart', {
    currency: 'USD',
    value: (item.price || 0) * (quantity || 1),
    items: [item],
  });
}

export function trackBeginCheckout(cartItems, value) {
  if (!cartItems?.length) return;
  safeLog('begin_checkout', {
    currency: 'USD',
    value,
    items: cartItems.map(cartItemToItem),
  });
}

export function trackPurchase(order) {
  if (!order) return;
  safeLog('purchase', {
    transaction_id: order.id,
    currency: 'USD',
    value: order.total,
    tax: order.tax,
    shipping: order.shipping_cost,
    items: (order.items || []).map((i) => ({
      item_id: i.product_id,
      item_name: i.product_name,
      item_variant: i.variant_name || undefined,
      price: i.unit_price,
      quantity: i.quantity,
    })),
  });
}

// ---- Firestore-backed view counter (Option B) ----

const VIEW_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes
const VIEW_KEY_PREFIX = 'pv:';

/**
 * Fire-and-forget call to bump a product's Firestore view counter.
 * Deduplicates per session: a single product won't be counted again
 * within VIEW_COOLDOWN_MS, so refreshing or back-navigating doesn't
 * inflate the number.
 */
export function trackProductPageView(productId) {
  if (!productId) return;
  try {
    const key = VIEW_KEY_PREFIX + productId;
    const last = Number(sessionStorage.getItem(key) || 0);
    if (last && Date.now() - last < VIEW_COOLDOWN_MS) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // sessionStorage can throw in private mode; just send the event.
  }
  api.post(`/products/${productId}/track-view`).catch(() => {
    // Tracking failures must never disrupt the user.
  });
}
